import _ from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  generatePath,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { AboutProject } from "../../components/Onboarding/AboutProject";
import { EmailOnboard } from "../../components/Onboarding/EmailOnboard";
import { Login } from "../../components/Onboarding/Login";
import { SignUp } from "../../components/Onboarding/SignUp";
import { OnboardingContext } from "../../context";
import AuthService from "../../service/authService";
import { COUNTRY, UserOnboardingInfo } from "../../types";
import { ONBOARDING_STEPS, ROUTES } from "../../constant/common.constant";
import { getLanguage } from "../../utils/LocaleUtil";
import { useTokenInfo } from "../../utils/ReactHooks";
import { useUserInfoFetcher } from "../../utils/UserHooks";
import { OnboardingTemplate } from "./OnboardingTemplate";
import { trackAccountCreated } from "../../utils/analytics.utils";
import { useTeam } from "../../context/Team";
import {
  createCheckEmailHandler,
  getSafeAttractionRedirectUrl,
  hasActiveStudioSession,
  createSignInHandler,
  validatePasswordStep,
} from "./signIn";

export const OnboardingContainer = () => {
  const { token: inviteToken } = useParams();
  //Change the language if the URL param set a specific language
  const location = useLocation();
  const { t } = useTranslation();
  const navigate = useNavigate();

  //Query parameter campaign (use for analytics)
  const campaign = useMemo(() => {
    const query = new URLSearchParams(location.search);
    return query.get("campaign");
  }, [location.search]);
  const redirectUrl = useMemo(() => {
    const query = new URLSearchParams(location.search);
    return getSafeAttractionRedirectUrl(query.get("redirect_url"));
  }, [location.search]);
  const getRedirectUrl = useCallback(() => {
    const query = new URLSearchParams(window.location.search);
    return getSafeAttractionRedirectUrl(query.get("redirect_url"));
  }, []);

  const [currentStep, setCurrentStep] = useState<ONBOARDING_STEPS>(
    ONBOARDING_STEPS.EMAIL
  );

  const [isAuthorizedDistributor, setIsAuthorizedDistributor] = useState(false);
  const { refetchTeam } = useTeam();
  const { initUserInfo } = useUserInfoFetcher();

  const [onboardingInfo, setOnboardingInfo] = useState<UserOnboardingInfo>({
    email: "",
    firstName: "",
    lastName: "",
    password: "",
    optNewsletter: true,
    language: "",
    country: COUNTRY.CA,
    forWho: undefined,
    contactPhone: "",
    projectSize: "",
    teamName: "",
    eventDate: undefined,
    campaign: "",
  });

  //Campaign url query parameter may not always be set
  useEffect(() => {
    setOnboardingInfo((infos) => ({
      ...infos,
      campaign: campaign || "",
    }));
  }, [campaign]);

  useEffect(() => {
    setOnboardingInfo((infos) => ({
      ...infos,
      language: getLanguage(),
    }));
  }, []);

  const [errorMsg, setErrorMsg] = useState("");
  const tokenInfo = useTokenInfo(inviteToken);

  useEffect(() => {
    if (!redirectUrl || inviteToken) {
      return;
    }

    let isMounted = true;

    hasActiveStudioSession()
      .then((hasSession) => {
        if (isMounted && hasSession) {
          window.location.assign(redirectUrl);
        }
      })
      .catch((error) => {
        console.error(error);
      });

    return () => {
      isMounted = false;
    };
  }, [inviteToken, redirectUrl]);

  const onCheckEmailNextStep = useCallback(
    createCheckEmailHandler({
      setErrorMsg,
      setOnboardingInfo,
      setIsAuthorizedDistributor,
      onGoToLogin: () => setCurrentStep(ONBOARDING_STEPS.LOGIN),
      onGoToSignup: () => setCurrentStep(ONBOARDING_STEPS.SIGNUP),
    }),
    []
  );

  /**
   *
   */
  const onBackToEmailStep = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      e.preventDefault();
      setCurrentStep(ONBOARDING_STEPS.EMAIL);
      setIsAuthorizedDistributor(false);
    },
    []
  );

  /**
   *
   */
  const onBackToUserInfoStep = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      e.preventDefault();
      setCurrentStep(ONBOARDING_STEPS.SIGNUP);
    },
    []
  );

  const onSignInClick = createSignInHandler({
    inviteToken,
    setErrorMsg,
    initUserInfo,
    refetchTeam,
    navigate,
    redirectUrl,
    getRedirectUrl,
  });

  /**
   * Last step
   */
  const onSignUpGetStarted = useCallback(
    async (values?: UserOnboardingInfo) => {
      setErrorMsg("");
      const formValues = values ?? onboardingInfo;
      //Dates can't be an empty string, so we must set eventDate to null if it is ""
      formValues.eventDate = undefined;

      formValues.inviteToken = inviteToken || undefined;

      formValues.language = getLanguage();
      const res = await AuthService.signUp(formValues).catch((error) => {
        return error.response;
      });

      if (!res?.data) {
        setErrorMsg(t("sign_up.network_error"));
        return;
      }

      const { data, message } = res.data;

      if (res.status < 200 || res.status >= 300) {
        if (message === "username_already_exists") {
          setErrorMsg(t("sign_up." + message, { email: formValues.email }));
          return;
        }
        setErrorMsg(t("sign_up." + message));
        return;
      }

      const { newUser, newProject, joinedTeam, newTeam } = data;

      //Equivalent of login right after signup
      AuthService.setLocalUser(newUser.currency, newUser.country);

      initUserInfo();

      //Analytics
      if (_.isEmpty(formValues.projectSize)) {
        trackAccountCreated({
          firstName: formValues.firstName ?? "",
          lastName: formValues.lastName ?? "",
          email: formValues.email ?? "",
          invited: inviteToken ? true : false, // whether the user signed up via an invitation link
          forWho: formValues.forWho,
          currency: newUser.currency,
          country: formValues.country ?? COUNTRY.CA,
          contactPhone: formValues.contactPhone,
        });
      } else {
        trackAccountCreated({
          firstName: formValues.firstName ?? "",
          lastName: formValues.lastName ?? "",
          email: formValues.email ?? "",
          invited: inviteToken ? true : false, // whether the user signed up via an invitation link
          projectSize: formValues.projectSize,
          label: `${formValues.forWho} / ${formValues.projectSize}`,
          campaign: formValues.campaign,
          currency: newUser.currency,
          forWho: formValues.forWho,
          country: formValues.country ?? COUNTRY.CA,
          contactPhone: formValues.contactPhone,
        });
      }

      ///if no invite token, then route to the newly created project and popup the create design
      if (!inviteToken) {
        navigate(
          generatePath(ROUTES.PROJECT, {
            projectId: newProject.id,
            teamId: newTeam.id,
          })
        );
        return;
      }

      //Push to joined team's page
      navigate(generatePath(ROUTES.TEAM, { teamId: joinedTeam.id }));
    },
    [setErrorMsg, onboardingInfo, initUserInfo, t, navigate, inviteToken]
  );

  const onCheckUserInfoNextStep = useCallback(
    async (values: UserOnboardingInfo) => {
      setErrorMsg("");
      const valid = await validatePasswordStep(
        values.password ?? "",
        t,
        setErrorMsg
      );
      if (!valid) return;
      setOnboardingInfo(values);
      setCurrentStep(ONBOARDING_STEPS.ABOUT_PROJECT);
    },
    [setCurrentStep, t]
  );

  return (
    <OnboardingContext.Provider value={{ errorMsg, tokenInfo }}>
      <OnboardingTemplate
        hideMarquee={currentStep === ONBOARDING_STEPS.ABOUT_PROJECT}
      >
        {currentStep === ONBOARDING_STEPS.EMAIL && (
          <EmailOnboard
            onNextStep={onCheckEmailNextStep}
            email={onboardingInfo.email}
          />
        )}
        {currentStep === ONBOARDING_STEPS.LOGIN && !!onboardingInfo.email && (
          <Login
            onNextStep={onSignInClick}
            onPreviousStep={onBackToEmailStep}
            email={onboardingInfo.email}
          />
        )}
        {currentStep === ONBOARDING_STEPS.SIGNUP && (
          <SignUp
            onNextStep={onCheckUserInfoNextStep}
            onPreviousStep={onBackToEmailStep}
            userInfo={onboardingInfo}
            setUserInfo={setOnboardingInfo}
          />
        )}
        {currentStep === ONBOARDING_STEPS.ABOUT_PROJECT && (
          <AboutProject
            onNextStep={onSignUpGetStarted}
            onPreviousStep={onBackToUserInfoStep}
            onboardingInfo={onboardingInfo}
            setOnboardingInfo={setOnboardingInfo}
            isAuthorizedDistributor={isAuthorizedDistributor}
            hasInviteToken={!!inviteToken}
          />
        )}
      </OnboardingTemplate>
    </OnboardingContext.Provider>
  );
};
